import React from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { serialize } from "next-mdx-remote/serialize";

import getTranslationByPath from "../services/getTranslationByPath.mjs";
import Container from "../components/Common/Container";
import RequestADemo from "../components/Common/RequestADemo";
import Hero from "../components/FocusFeature/Hero";
import Description from "../components/FocusFeature/Description";
import Benefits from "../components/FocusFeature/Benefits";
import Admin from "../components/FocusFeature/Admin";
import Resource from "../components/FocusFeature/Resource";
import importAll from "../lib/utils/image";

import heroImage from "../public/static/images/participatory-budget/hero.png";
import descriptionImage from "../public/static/images/participatory-budget/description.jpg";
import adminImage from "../public/static/images/participatory-budget/etape-bp-admin.gif";
import resourceImage from "../public/static/images/participatory-budget/livre-blanc-budget-participatif.png";
import MetaTags from "../components/Common/MetaTags";
import CloudinaryImage from "../components/Common/CloudinaryImage/CloudinaryImage.js";

const imagesBenefits = importAll(
  require.context(
    "../public/static/images/participatory-budget/benefits",
    false,
    /\.(png|jpe?g|svg)$/
  )
);

export default function ParticipatoryBudget({
  heroSubtitle,
  descriptionContent,
}) {
  const theme = useTheme();
  const { t } = useTranslation(["participatory-budget", "common"]);

  return (
    <>
      <MetaTags scope="participatory-budget" />
      <Box bgcolor={theme.palette.alternate.main} position={"relative"}>
        <Container position="relative" zIndex={2}>
          <Hero
            pageTitle={t("navbar.participatory_budget", { ns: "common" })}
            title1={t("hero.title_1")}
            title2={t("hero.title_2")}
            title3={t("hero.title_3")}
            subtitle={heroSubtitle}
            image={
              <CloudinaryImage
                src={heroImage}
                alt={`${t("page.title")} hero`}
                width={258}
                height={304}
                placeholder="blur"
              />
            }
          />
        </Container>
        <Box
          component={"svg"}
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          x="0px"
          y="0px"
          viewBox="0 0 1921 273"
          sx={{
            position: "absolute",
            width: "100%",
            left: 0,
            bottom: 0,
            right: 0,
            zIndex: 1,
            height: "35%",
          }}
        >
          <polygon
            fill={theme.palette.background.paper}
            points="0,273 1921,273 1921,0 "
          />
        </Box>
      </Box>

      <Container>
        <Description
          title1={t("description.title_1")}
          title2={t("description.title_2")}
          content={descriptionContent}
          image={
            <CloudinaryImage
              src={descriptionImage}
              alt={`${t("page.title")} illustration`}
              width={525}
              height={405}
              placeholder="blur"
            />
          }
        />
      </Container>

      <Box
        component={"svg"}
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        x="0px"
        y="0px"
        viewBox="0 0 1921 273"
        sx={{
          marginBottom: theme.spacing(-1),
        }}
      >
        <polygon
          fill={theme.palette.alternate.main}
          points="0,273 1921,273 1921,0 "
        />
      </Box>
      <Box bgcolor={theme.palette.alternate.main} position={"relative"}>
        <Container position="relative" zIndex={2}>
          <Benefits
            title1={t("benefits.title_1")}
            title2={t("benefits.title_2")}
            title3={t("benefits.title_3")}
            blocks={t("benefits.blocks", { returnObjects: true })}
            images={imagesBenefits}
          />
        </Container>
        <Box
          component={"svg"}
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          x="0px"
          y="0px"
          viewBox="0 0 1921 273"
          sx={{
            position: "absolute",
            width: "100%",
            left: 0,
            bottom: 0,
            right: 0,
            zIndex: 1,
            height: "35%",
          }}
        >
          <polygon
            fill={theme.palette.background.paper}
            points="0,273 1921,273 1921,0 "
          />
        </Box>
      </Box>

      <Container>
        <Admin
          title1={t("admin.title_1")}
          title2={t("admin.title_2")}
          blocks={t("admin.blocks", { returnObjects: true })}
          image={
            <CloudinaryImage
              src={adminImage}
              alt={`${t("page.title")} administration illustration`}
              width={638}
              height={456}
            />
          }
        />
      </Container>

      <Box bgcolor={theme.palette.alternate.main}>
        <Container>
          <Resource
            header={t("resource.header")}
            title={t("resource.title")}
            subtitle={t("resource.subtitle")}
            ctaTitle={t("resource.cta")}
            ctaHref="/static/resources/livre-blanc-budget-participatif-fluicity.pdf"
            image={
              <CloudinaryImage
                src={resourceImage}
                alt={`${t("page.title")} resource illustration`}
                width={245}
                height={300}
                priority
              />
            }
          />
        </Container>
      </Box>

      <RequestADemo title={t("request_a_demo.title")} />
    </>
  );
}

export async function getStaticProps({ locale }) {
  const { content } = getTranslationByPath("participatory-budget", locale);

  return {
    props: {
      ...(await serverSideTranslations(locale, [
        "common",
        "participatory-budget",
      ])),
      heroSubtitle: await serialize(content.hero.subtitle),
      descriptionContent: await serialize(content.description.content),
    },
  };
}
